import { computed, type Ref } from 'vue';
import type {
  AnyDrillStrategy,
  DrillAnswerMap,
  DrillContext,
  DrillItemMap,
  DrillKind,
} from '../utilities/drillStrategies/types';

type DrillItem = DrillItemMap[DrillKind];
type DrillAnswer = DrillAnswerMap[DrillKind];

export interface UseDrillAnswersOptions {
  /** The resolved kind's strategy (Theory/unknown → null). */
  strategy: Readonly<Ref<AnyDrillStrategy | null>>;
  drillContext: Readonly<Ref<DrillContext>>;
  currentStackItem: Readonly<Ref<unknown>>;
}

/**
 * The current question's answers through the drill strategy, with useStep's
 * guards and no-strategy fallbacks (moved verbatim from useStep, plus
 * StepView's help text).
 */
export function useDrillAnswers({
  strategy,
  drillContext,
  currentStackItem,
}: UseDrillAnswersOptions) {
  /** The current item as the strategy's item (shape per kind). */
  const strategyItem = () => currentStackItem.value as DrillItem;

  function answerText(answer: unknown, drillTextContent?: string) {
    if (!strategy.value) return answer;
    return strategy.value.spokenAnswer(
      answer as DrillAnswer,
      drillTextContent as string,
    );
  }

  function checkAnswer(answer: unknown) {
    if (!currentStackItem.value) return undefined;
    if (answer === undefined || answer === null) return false;
    if (!strategy.value) return false;
    return strategy.value.isCorrect(
      answer as DrillAnswer,
      strategyItem(),
      drillContext.value,
    );
  }

  // Typed `any` as before the strategies (its per-kind shapes; StepView and
  // the useStep suites read it per kind) until the R6 test migration.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const currentCorrectAnswer = computed((): any => {
    if (!strategy.value) return null;
    return strategy.value.correctAnswer(strategyItem(), drillContext.value);
  });

  function getFeedbackAnswer(answer: unknown) {
    if (!strategy.value) return answer as string;
    return strategy.value.feedbackAnswerHtml(
      answer as DrillAnswer,
      strategyItem(),
      drillContext.value,
    );
  }

  function getCorrectFeedbackAnswer() {
    if (!strategy.value) return currentCorrectAnswer.value as string;
    return strategy.value.feedbackCorrectAnswer(
      strategyItem(),
      drillContext.value,
    ) as string;
  }

  function getFeedbackSubtitle(drillText: string) {
    if (!strategy.value) return drillText;
    return strategy.value.feedbackSubtitle(drillText, strategyItem());
  }

  /** The HELP dialog's correct answer (StepView's getHelp). */
  function helpAnswer() {
    return strategy.value
      ? strategy.value.helpAnswer(strategyItem(), drillContext.value)
      : currentCorrectAnswer.value;
  }

  return {
    currentCorrectAnswer,
    checkAnswer,
    getFeedbackAnswer,
    getCorrectFeedbackAnswer,
    getFeedbackSubtitle,
    answerText,
    helpAnswer,
  };
}

export type UseDrillAnswersReturn = ReturnType<typeof useDrillAnswers>;
